import React from 'react'
import { connect } from 'react-redux'
import TextareaAutoSize from 'react-textarea-autosize'
import debounce from 'lodash/debounce'

import { actions as NotesActions } from 'data/notes'

export const NOTE_TITLE_ID = 'NOTE_TITLE_ID'

const SAVE_DEBOUNCE = 1000
const MAX_TITLE_LENGTH = '250'

const INPUT_STYLE = {
  border: 0,
  outline: 0,
  resize: 'none',
  background: 'transparent',
}

const NOOP = x => x

function NoteTitle(
  {
    noteId,
    noteTitle: originalTitle = '',
    updateNoteAttributes = NOOP,
    readOnly = false,
    focusNext = NOOP,
  },
  ref
) {
  const [title, setTitle] = React.useState(originalTitle)

  React.useEffect(
    function onOriginalTitleChange() {
      setTitle(originalTitle)
    },
    [originalTitle]
  )

  React.useEffect(
    // We focus on noteId change, if and only if original title is
    () => {
      if (ref.current && originalTitle === '') {
        ref.current.focus()
      }
    },
    [ref, noteId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const debouncedUpdateNoteAttributes = React.useMemo(
    () => debounce(updateNoteAttributes, SAVE_DEBOUNCE),
    [updateNoteAttributes]
  )

  const onChange = React.useCallback(
    event => {
      const newTitle = event.target.value.replace(/[\r\n]+/g, '')
      if (newTitle === title) {
        return false
      }

      setTitle(newTitle)

      if (newTitle === originalTitle) {
        return
      }

      debouncedUpdateNoteAttributes({ title: newTitle, noteId })
    },
    [title, noteId, originalTitle, debouncedUpdateNoteAttributes]
  )

  const onKeyDown = React.useCallback(
    event => {
      if (event.shiftKey) {
        return
      }

      if (event.key === 'Enter') {
        event.stopPropagation()
        event.preventDefault()
        focusNext({ at: 0, insertLine: true })
      } else if (event.key === 'ArrowDown') {
        event.stopPropagation()
        event.preventDefault()
        const input = ref.current
        const at = input ? input.selectionStart || 0 : 0
        focusNext({ at })
      }
    },
    [ref, focusNext]
  )

  return (
    <div
      className="flexbox position-relative fs-block data-hj-suppress" /* fs-block & data-hj-suppress hides content for FullStory & Hotjar */
    >
      {readOnly ? (
        <div
          id={NOTE_TITLE_ID}
          className="font-family-system font-weight-bold note-editor-title flexbox-grow"
        >
          {title || <span className="shade-20">Untitled</span>}
        </div>
      ) : (
        <TextareaAutoSize
          inputRef={ref}
          id={NOTE_TITLE_ID}
          minRows={1}
          maxRows={3}
          maxLength={MAX_TITLE_LENGTH}
          className="font-family-system font-weight-bold note-editor-title flexbox-grow placeholder-shade-30"
          style={INPUT_STYLE}
          placeholder="Give your doc a title"
          value={title}
          onInput={onChange}
          onKeyDown={onKeyDown}
        />
      )}
    </div>
  )
}

export default connect(
  null,
  { updateNoteAttributes: NotesActions.updateNoteAttributes },
  null,
  { forwardRef: true }
)(React.forwardRef(NoteTitle))
